import { useEffect, useRef, useState, useCallback } from "preact/hooks";

const NEAR_BOTTOM_THRESHOLD = 80;
const NEAR_TOP_THRESHOLD = 150;
const OVERSCROLL_PADDING = 500;
const MAX_MESSAGES = 500;
const UNLOAD_BUFFER = 50;

interface UseScrollLockOptions {
  /** Called when the user scrolls to near the top and older messages should load. */
  onLoadOlder: () => void;
  /** Whether an older-messages load is already in flight. */
  isLoadingOlder: boolean;
  /** Called after older messages have been prepended and scroll position compensated. */
  onOlderLoaded: () => void;
  /** Optional: called when messages should be unloaded from one end */
  onUnloadMessages?: (count: number, fromStart: boolean) => void;
  /** Optional: called when user scrolls near bottom but not at bottom to load newer messages */
  onLoadNewer?: () => void;
  /** Optional: whether a newer-messages load is already in flight */
  isLoadingNewer?: boolean;
  /** Optional: called after newer messages have been appended */
  onNewerLoaded?: () => void;
}

function useStableCallback<T extends (...args: any[]) => any>(fn: T): T {
  const ref = useRef<T>(fn);
  ref.current = fn;
  return useCallback((...args: any[]) => ref.current(...args), []) as T;
}

interface UseScrollLockResult {
  containerRef: { current: HTMLDivElement | null };
  showScrollBtn: boolean;
  scrollToBottom: () => void;
  scrollToMessage: (messageId: string) => void;
  resetForChannel: () => void;
  beginLoadOlder: () => void;
  beginLoadNewer: () => void;
  overscrollPadding: number;
}

export function useScrollLock({
  onLoadOlder,
  isLoadingOlder,
  onOlderLoaded,
  onUnloadMessages,
  onLoadNewer,
  isLoadingNewer,
  onNewerLoaded,
}: UseScrollLockOptions): UseScrollLockResult {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const autoScroll = useRef(true);
  const pendingOlderLoad = useRef(false);
  const pendingNewerLoad = useRef(false);
  const loadOlderDebounce = useRef<number | null>(null);
  const loadNewerDebounce = useRef<number | null>(null);
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const isScrollingProgrammatically = useRef(false);
  const scrollRAF = useRef<number | null>(null);
  const prevScrollTop = useRef(0);
  const lastScrollDirection = useRef<"up" | "down" | null>(null);

  const stableOnLoadOlder = useStableCallback(onLoadOlder);
  const stableOnOlderLoaded = useStableCallback(onOlderLoaded);
  const stableOnLoadNewer = onLoadNewer ? useStableCallback(onLoadNewer) : null;
  const stableOnNewerLoaded = onNewerLoaded
    ? useStableCallback(onNewerLoaded)
    : null;
  const isLoadingOlderRef = useRef(isLoadingOlder);
  isLoadingOlderRef.current = isLoadingOlder;
  const isLoadingNewerRef = useRef(isLoadingNewer || false);
  isLoadingNewerRef.current = isLoadingNewer || false;

  const scrollToBottom = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;

    cancelAnimationFrame(scrollRAF.current!);
    isScrollingProgrammatically.current = true;
    scrollRAF.current = requestAnimationFrame(() => {
      el.scrollTo({
        top: el.scrollHeight,
        behavior: "smooth",
      });
      autoScroll.current = true;
      setShowScrollBtn(false);
      setTimeout(() => {
        isScrollingProgrammatically.current = false;
      }, 500);
    });
  }, []);

  const scrollToMessage = useCallback((messageId: string) => {
    const el = containerRef.current;
    if (!el) return;

    requestAnimationFrame(() => {
      const targetEl = el.querySelector(`[data-msg-id="${messageId}"]`);
      if (targetEl) {
        isScrollingProgrammatically.current = true;
        targetEl.scrollIntoView({ behavior: "smooth", block: "center" });
        targetEl.classList.add("highlight-flash");
        setTimeout(() => {
          targetEl.classList.remove("highlight-flash");
          isScrollingProgrammatically.current = false;
        }, 2000);
      }
    });
  }, []);

  const resetForChannel = useCallback(() => {
    autoScroll.current = true;
    pendingOlderLoad.current = false;
    setShowScrollBtn(false);
    lastScrollDirection.current = null;
    if (loadOlderDebounce.current !== null) {
      clearTimeout(loadOlderDebounce.current);
      loadOlderDebounce.current = null;
    }
  }, []);

  const beginLoadOlder = useCallback(() => {
    pendingOlderLoad.current = true;
  }, []);

  const beginLoadNewer = useCallback(() => {
    pendingNewerLoad.current = true;
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onScroll = () => {
      if (isScrollingProgrammatically.current) return;

      const distanceFromBottom =
        el.scrollHeight - el.scrollTop - el.clientHeight;
      const nearBottom = distanceFromBottom < NEAR_BOTTOM_THRESHOLD;

      const currentScrollTop = el.scrollTop;
      const scrollDirection =
        currentScrollTop < prevScrollTop.current ? "up" : "down";
      if (scrollDirection !== lastScrollDirection.current) {
        lastScrollDirection.current = scrollDirection;
      }
      prevScrollTop.current = currentScrollTop;

      autoScroll.current = nearBottom;
      setShowScrollBtn(!nearBottom);

      // Load older messages when near top (with overscroll support)
      if (
        el.scrollTop <= NEAR_TOP_THRESHOLD &&
        !isLoadingOlderRef.current &&
        !pendingOlderLoad.current
      ) {
        if (loadOlderDebounce.current !== null) return;
        loadOlderDebounce.current = window.setTimeout(() => {
          loadOlderDebounce.current = null;
          const container = containerRef.current;
          if (
            !container ||
            container.scrollTop > NEAR_TOP_THRESHOLD ||
            pendingOlderLoad.current
          )
            return;
          stableOnLoadOlder();
        }, 50);
      }

      // Load newer messages when near bottom but not at bottom
      if (
        stableOnLoadNewer &&
        nearBottom &&
        !autoScroll.current &&
        distanceFromBottom > 5 &&
        !pendingNewerLoad.current &&
        !isLoadingNewerRef.current
      ) {
        if (loadNewerDebounce.current !== null) return;
        loadNewerDebounce.current = window.setTimeout(() => {
          loadNewerDebounce.current = null;
          if (pendingNewerLoad.current || isLoadingNewerRef.current) return;
          stableOnLoadNewer();
        }, 300);
      }
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
      if (loadOlderDebounce.current !== null) {
        clearTimeout(loadOlderDebounce.current);
        loadOlderDebounce.current = null;
      }
      if (loadNewerDebounce.current !== null) {
        clearTimeout(loadNewerDebounce.current);
        loadNewerDebounce.current = null;
      }
    };
  }, [stableOnLoadOlder, stableOnLoadNewer]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    let prevScrollHeight = el.scrollHeight;
    let rafId: number | null = null;

    const observer = new MutationObserver(() => {
      const newScrollHeight = el.scrollHeight;
      const heightAdded = newScrollHeight - prevScrollHeight;
      prevScrollHeight = newScrollHeight;

      if (pendingOlderLoad.current && heightAdded > 0) {
        cancelAnimationFrame(rafId!);
        rafId = requestAnimationFrame(() => {
          el.scrollTop += heightAdded;
        });
        pendingOlderLoad.current = false;
        if (
          el.scrollTop <= NEAR_TOP_THRESHOLD &&
          loadOlderDebounce.current === null
        ) {
          loadOlderDebounce.current = window.setTimeout(() => {
            loadOlderDebounce.current = null;
          }, 50);
        }
        stableOnOlderLoaded();
        return;
      }

      if (pendingNewerLoad.current && heightAdded > 0) {
        pendingNewerLoad.current = false;
        if (stableOnNewerLoaded) {
          stableOnNewerLoaded();
        }
        return;
      }

      if (autoScroll.current) {
        cancelAnimationFrame(rafId!);
        rafId = requestAnimationFrame(() => {
          el.scrollTop = newScrollHeight;
        });
      }
    });

    observer.observe(el, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [stableOnOlderLoaded, stableOnNewerLoaded]);

  useEffect(() => {
    return () => {
      if (scrollRAF.current) cancelAnimationFrame(scrollRAF.current);
    };
  }, []);

  return {
    containerRef,
    showScrollBtn,
    scrollToBottom,
    scrollToMessage,
    resetForChannel,
    beginLoadOlder,
    beginLoadNewer,
    overscrollPadding: OVERSCROLL_PADDING,
  };
}
